import { IFluidCodeDetails } from '@fluidframework/core-interfaces';
import { DriverInfo } from '@ms/office-web-host';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { assertNever } from 'owa-assert';
import { timestamp } from 'owa-datetime';
import { EditorViewState, lazyOperateContent, OperateContentType } from 'owa-editor';
import { onFluidPlaceholderCreated } from 'owa-fluid-link';
import { getGuid } from 'owa-guid';
import { onFluidLinkCreated } from 'owa-link-data';
import loc from 'owa-localize';
import * as trace from 'owa-trace';
import React, { useEffect } from 'react';
import * as ReactDOM from 'react-dom';
import { PositionType } from 'roosterjs-editor-types';
import { getFluidLink } from '../selectors/getFluidLink';
import { FluidLinkViewState } from '../store/schema/FluidLinkViewState';
import { getDriverInfoForNewFile } from '../utils/getDriverInfoForNewFile';
import {
    FluidOwaSource,
    FluidOwaScenario,
    FluidLoadOwaScenario,
    FluidLoadSource,
} from 'owa-fluid-validations';
import { mountNewFluidComponent } from '../utils/mountComponent/mountNewFluidComponent';
import { fluidConversionPlaceholderText } from './FluidLinkConverter.locstring.json';
import { FluidContainerConfigForEditor } from '../store/schema/FluidContainerConfig';
import { fluidComponentError } from 'owa-fluid-datapoints';

interface FluidLinkConverterProps {
    editorViewState: EditorViewState;
    originalNode: Node;
    driverInfo: DriverInfo;
    codeDetails: IFluidCodeDetails;
    containerId: string;
    linkId: string;
    operateContent: OperateContentType;
    scenarioStartTime: number;
    owaSource: FluidOwaSource;
    addRecipientFunc: (address: string) => void;
    viewStateId?: string;
}

const FluidLinkConverter = observer(function FluidLinkConverter(props: FluidLinkConverterProps) {
    const fluidLinkConverterRef: React.RefObject<HTMLDivElement> = React.createRef();
    const viewState: FluidLinkViewState | undefined = getFluidLink(props.containerId);

    useEffect(() => {
        if (viewState?.url) {
            props.operateContent(props.editorViewState, (editor, range, domUtils) => {
                const replaceTargets: HTMLElement[] = editor.queryElements('#' + props.linkId);

                if (replaceTargets.length !== 1) {
                    trace.errorThatWillCauseAlert(
                        `FluidifyLink found incorrect number of links to replace. Count is ${replaceTargets.length}`
                    );
                    return null;
                }

                const newAnchor: HTMLAnchorElement = editor.getDocument().createElement('a');
                newAnchor.href = viewState.url;
                newAnchor.innerText = viewState.url;

                // We need to replace the child only so we keep any text typed under the parent element before the new anchor
                editor.replaceNode(replaceTargets[0], newAnchor);
                onFluidLinkCreated(
                    props.linkId,
                    props.containerId,
                    props.owaSource,
                    viewState.url,
                    props.viewStateId
                );

                return null;
            });
        }
    }, [viewState?.url]);

    useEffect(() => {
        // Mount event
        const fluidLinkConverter: HTMLSpanElement | null = fluidLinkConverterRef.current;

        if (fluidLinkConverter) {
            fluidLinkConverter.setAttribute('contenteditable', 'false');

            props.operateContent(props.editorViewState, (editor, range, domUtils) => {
                editor.insertNode(fluidLinkConverter);
                const spaceNode = editor.getDocument().createTextNode('\u00A0');
                editor.insertNode(spaceNode);

                // Then we insert the iframe to create the new component
                const scenario = getScenarioFromSource(props.owaSource);

                const containerConfig: FluidContainerConfigForEditor = {
                    configType: 'editor',
                    editorViewState: props.editorViewState,
                    editor,
                    containerId: props.containerId,
                };

                mountNewFluidComponent(
                    containerConfig,
                    fluidLinkConverterRef.current,
                    props.driverInfo,
                    props.codeDetails,
                    props.scenarioStartTime,
                    props.owaSource,
                    scenario,
                    props.addRecipientFunc,
                    undefined, // ComponentLoaderData
                    props.viewStateId
                );
                return domUtils.createRange(fluidLinkConverter, PositionType.After);
            });
        }
    }, []);

    const inlineStyle: React.CSSProperties = {
        backgroundColor: 'rgb(244,244,244)',
        display: 'inline-block',
    };

    return (
        <div style={inlineStyle} ref={fluidLinkConverterRef} id={props.linkId}>
            {/*VSO 94822 - improve the header content while creating the fluid file */}
            {loc(fluidConversionPlaceholderText)}
        </div>
    );
});

export async function fluidifyLink(
    editorViewState: EditorViewState,
    originalNode: Node,
    codeDetails: IFluidCodeDetails,
    owaSource: FluidOwaSource,
    document: Document,
    addRecipientFunc: (address: string) => void,
    viewStateId: string
) {
    const scenarioStartTime = timestamp();

    logUsage(
        'fluidifyLink',
        {
            owaSource: owaSource,
        },
        { isCore: true }
    );

    // We track the element using the id of the container we will insert it into. This is because the converter
    // is not in the DOM while the fluid component is first loading, so it will be missed when we query it.
    const containerId = 'fluidContainer' + getGuid();
    const linkId = 'fluidLink' + getGuid();
    onFluidPlaceholderCreated(containerId, owaSource, viewStateId);
    const anchorContainer = document.createElement('span');

    let driverInfo = null;
    try {
        driverInfo = await getDriverInfoForNewFile(codeDetails, owaSource);
    } catch (error) {
        fluidComponentError(
            FluidLoadOwaScenario.ComposeWithNewComponent,
            owaSource,
            FluidLoadSource.Bohemia,
            error
        );

        return;
    }

    // If we have a text node we need to convert it to an anchor to render it.
    let newNode;
    if (originalNode.nodeType === 3) {
        newNode = document.createElement('a');
        newNode.href = originalNode.textContent;
        newNode.appendChild(originalNode);
    } else {
        newNode = originalNode;
    }

    // use operate content to insert the div into the editor.
    const operateContent = await lazyOperateContent.import();
    ReactDOM.render(
        <React.StrictMode>
            <FluidLinkConverter
                editorViewState={editorViewState}
                originalNode={newNode}
                codeDetails={codeDetails}
                driverInfo={driverInfo}
                containerId={containerId}
                linkId={linkId}
                operateContent={operateContent}
                scenarioStartTime={scenarioStartTime}
                owaSource={owaSource}
                addRecipientFunc={addRecipientFunc}
                viewStateId={viewStateId}
            />
        </React.StrictMode>,
        anchorContainer
    );
}

function getScenarioFromSource(source: FluidOwaSource): FluidOwaScenario {
    switch (source) {
        case FluidOwaSource.MailCompose:
            return FluidOwaScenario.MAIL_FLUIDIFY;
        case FluidOwaSource.AgendaEditor:
        case FluidOwaSource.CalendarCompose:
            return FluidOwaScenario.CALENDAR_FLUIDIFY;
        case FluidOwaSource.MailReadingPane:
        case FluidOwaSource.MailCalendarCard:
        case FluidOwaSource.CalendarReadingPane:
        case FluidOwaSource.Timestream:
        case FluidOwaSource.None:
            trace.errorThatWillCauseAlert(
                `Incompatible fluid source for fluidifing link. Source is ${source}`
            );
            return FluidOwaScenario.UNKNOWN_FLUIDIFY;
        default:
            return assertNever(source);
    }
}
